// @ts-check

// Vendor imports
const _ = require('lodash')
const Bowser = require('bowser')
const uuid = require('uuid/v4')
// Client lib imports
const { send: ajax } = require('client/lib/request')
// Sibling imports
const branchIntegration = require('./branch')
const globalPageContext = require('./page-context')
const protobufCommands = require('./protobuf-commands')
const segmentIntegration = require('./segment')

/**
 * This module is only intended for use in a client-side web-browser execution context;
 * however it is incidentally required on Node during testing and server-side rendering.
 * This conditional is to prevent `require('client/lib/business-events')` from failing;
 * `getAppContext` should never be called outside of the browser and should fail if it ever is.
 * @type {Bowser.Parser.Parser}
 */
const browser = typeof navigator !== 'undefined'
? Bowser.getParser(navigator.userAgent)
: null

const NOT_CONFIGURED = () => { throw new Error('client/lib/business-events is not configured!') }

/** @typedef { import('./types.d').DenormalizedPageContext } DenormalizedPageContext */
/** @typedef { import('./types.d').PageContext } PageContext */
/** @typedef { import('./types.d').PageContextWithReferrer } PageContextWithReferrer */
/** @typedef { import('./types.d').ClientSession } ClientSession */
/** @typedef { import('./types.d').UtmParams } UtmParams */
/** @typedef { import('./types.d').User } User */

/** @typedef {function(PageContextWithReferrer): string} getPageNameCallback */
/** @typedef {function(): string} getSessionIdCallback */

// Configuration for client v1-v2 cross-compatibility
/** @type {string|number} */
let userId
/** @type {boolean} */
let isBot
/** @type {boolean} */
let isLoggedIn
/** @type {string} */
let buildNumber
/** @type {Object<string, string>} */
let experiments = {}
/** @type {getPageNameCallback} */
let getPageName = NOT_CONFIGURED
/** @type {getSessionIdCallback} */
let getSessionId = NOT_CONFIGURED

// Attempt to auto-configure for v1 (wiggin) client
if (typeof wiggin !== 'undefined') {
  configure({
    // @ts-ignore global wiggin types are not defined
    user: wiggin.user,
    // @ts-ignore global wiggin types are not defined
    buildNumber: wiggin.buildNumber,
    getPageName: pageContext => pageContext['page_name'] || app.state('page'),
    getSessionId: () => app.sess.update()
  })
}

/**
 * @param {Object} options
 * @param {Object} [options.user]
 * @param {boolean} options.user.bot
 * @param {Object<string, string>} options.user.experiments
 * @param {string|number} options.user.id
 * @param {boolean} options.user.loggedIn
 * @param {string} [options.buildNumber]
 * @param {getPageNameCallback} [options.getPageName]
 * @param {getSessionIdCallback} [options.getSessionId]
 */
function configure(options) {
  if (options.user) {
    userId = options.user.id
    isBot = options.user.bot
    isLoggedIn = options.user.loggedIn
    experiments = options.user.experiments
  }
  if (options.buildNumber) {
    buildNumber = options.buildNumber
  }
  if (options.getPageName) {
    getPageName = options.getPageName
  }
  if (options.getSessionId) {
    getSessionId = options.getSessionId
  }
}

/**
 * @param {User} user
 */
async function identifyUser(user) {
  await Promise.all([
    branchIntegration.identifyUser(user),
    segmentIntegration.identifyUser(user)
  ])
}

/**
 * Send a tracking call to our internal track API
 * @param {string} eventName
 * @param {Object<string, any>} [properties]
 * @param {Object} [options]
 * @param {DenormalizedPageContext} [options.pageContext]
 * @param {string} [options.sessionId]
 * @param {UtmParams} [options.utmParams]
 */
async function trackInternal(eventName, properties = {}, options = {}) {
  // We do not track internal events for bots or logged out users.
  if (isBot || !isLoggedIn) {
    return
  }

  // Overlay the options context on top of the page context
  const pageContext = getEventPageContext(options.pageContext)
  const sessionId = options.sessionId || getSessionId()
  /** @type {ClientSession} */
  const clientSession = { 'session_id': sessionId }
  if (options.utmParams) {
    clientSession['extra_params'] = { utm: options.utmParams }
  }

  const data = {
    action: 'track',
    event: getInternalEventType(eventName),
    'session_id': sessionId,
    'request_id': uuid(),
    'user_id': userId,
    'build_number': buildNumber,
    'page_context': pageContext,
    context: getAppContext(),
    properties: {
      ...properties,
      'client_session': clientSession,
      'page_name': getPageName(pageContext),
      'resulting_action_type': eventName
    }
  }

  await ajax({
    url: '/api/v0.1/track',
    method: 'post',
    contentType: 'application/json',
    data,
    sessionId
  })
  .catch(() => {}) // Ignore tracking errors
}

/**
 * @param {string} commandType
 * @param {any} params
 * @param {Object} [options]
 * @param {number} [options.eventTimestamp]
 * @param {DenormalizedPageContext} [options.pageContext]
 * @param {string} [options.sessionId]
 */
async function trackProtobuf(commandType, params, options = {}) {
  // We do not track events for bots or logged out users.
  if (isBot || !isLoggedIn) {
    return
  }
  // Capture the time of the event, then wait for commands to load
  const eventTimestamp = options.eventTimestamp || Date.now()
  await protobufCommands.sendCommand(commandType, params, {
    browser,
    buildNumber,
    eventTimestamp,
    pageContext: getEventPageContext(options.pageContext),
    sessionId: options.sessionId || getSessionId()
  })
}

/**
 * Send a tracking call using the Segment SDK
 * @param {string} eventName
 * @param {Object<string, any>} [properties]
 * @see https://segment.com/docs/spec/track/#properties
 * @param {Object} [options]
 * @param {DenormalizedPageContext} [options.pageContext]
 */
async function trackSegment(eventName, properties = {}, options = {}) {
  // We do not track Segment events for bot users
  if (isBot) {
    return
  }

  const pageContext = getEventPageContext(options.pageContext)

  await segmentIntegration.track(eventName, {
    ...properties,
    page: getPageName(pageContext),
    'test_groups': getTestGroups(),
    'branch_params': branchIntegration.getBusinessEventParams()
  })
  .catch(() => {}) // Ignore tracking errors
}

module.exports = {
  configure,
  identifyUser,
  trackProtobuf,
  trackInternal,
  trackSegment
}

/**
 * @param {string} eventName
 */
function getInternalEventType(eventName) {
  switch (eventName) {
    case 'search-topic':
      return 'search'

    case 'pageview':
      return 'page_load'

    case 'paginate':
      return 'interaction_swipe'

    default:
      return 'interaction_tap'
  }
}

/**
 * @param {DenormalizedPageContext} pageContextOverlay
 * @returns {PageContextWithReferrer}
 */
function getEventPageContext(pageContextOverlay) {
  return _.merge(
    // Some browsers omit empty objects; __keep ensures the page context is always sent.
    { '__keep': true },
    globalPageContext.getPageContext(),
    globalPageContext.normalizePageContext(pageContextOverlay)
  )
}

function getAppContext() {
  const { innerWidth, innerHeight } = window
  const { clientWidth, clientHeight } = document.documentElement
  return {
    'app_name': browser.getBrowserName(),
    'app_version': browser.getBrowserVersion(),
    'screen_height': Math.max(clientHeight, innerHeight),
    'screen_width': Math.max(clientWidth, innerWidth)
  }
}

/**
 * Convert experiments Object into a comma-separated key=value string
 */
function getTestGroups() {
  return Object.entries(experiments)
  .map(pair => pair.join('='))
  .join(',')
}
